import React, { ChangeEvent } from "react";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../ui-kit/kuika-modal-icon-wrapper";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaText from "../../ui-kit/typography/Text/Text";
import "./branching-modal.scss";

export interface ICreateBranchUncommittedModalProps {
  showModal: boolean;
  changeCount: number;
  commitMessage: string;
  inProgress?: boolean;
  /**
   * Where the create stands (`KuikaButton` `status`), drawn on the button that ran it —
   * `statusAction` says which. Given, `inProgress` no longer draws on that button.
   */
  status?: KuikaButtonStatus;
  statusAction?: "commit" | "without";
  onClose: () => void;
  onCommitMessageChange: (value: string) => void;
  onCommitAndCreate: () => void;
  onCreateWithoutCommitting: () => void;
}

const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

const CreateBranchUncommittedModal: React.FC<ICreateBranchUncommittedModalProps> = ({
  showModal,
  changeCount,
  commitMessage,
  inProgress,
  status,
  statusAction,
  onClose,
  onCommitMessageChange,
  onCommitAndCreate,
  onCreateWithoutCommitting
}) => {
  // Fixed-layout footer: KuikaButton shows its spinner inside the button, so the
  // loading state never changes a button's width and the row never reflows.
  const withoutStatus: KuikaButtonStatus | undefined = statusAction === "without" ? status : undefined;
  const commitStatus: KuikaButtonStatus | undefined = statusAction === "commit" ? status : undefined;
  const isBusy: boolean = !!inProgress || status === "loading" || status === "success";
  const footer = (
    <KModalFooter
      submitLabel="Commit & create"
      onSubmit={onCommitAndCreate}
      loading={inProgress}
      status={commitStatus}
      statusLabels={CREATE_STATUS_LABELS}
      disabled={commitStatus === undefined && (isBusy || !commitMessage.trim())}
    >
      <KuikaButton
        type="secondary"
        color="neutral"
        size="md"
        uppercase
        onClick={onCreateWithoutCommitting}
        status={withoutStatus}
        statusLabels={CREATE_STATUS_LABELS}
        disabled={withoutStatus === undefined && isBusy}
        data-testid="createBranchWithoutCommittingButton"
      >
        Create without committing
      </KuikaButton>
    </KModalFooter>
  );

  return (
    <KModal
      showModal={showModal}
      closeModal={onClose}
      header="Uncommitted Changes"
      footer={footer}
      width={460}
      centered
    >
      <div className="kuika-branching-modal">
        <div className="kuika-branching-modal__header">
          <KuikaModalIconWrapper iconName="git-commit" intent="warning" />
          <div className="kuika-branching-modal__title">
            <KuikaH5>Uncommitted Changes</KuikaH5>
          </div>
        </div>
        <div className="kuika-branching-modal__content">
          <div className="kuika-branching-modal__description">
            <KuikaText>
              The source branch has {changeCount} uncommitted change{changeCount === 1 ? "" : "s"}. The new branch is
              created from the last committed state — commit them first to include them.
            </KuikaText>
          </div>
          <div className="kuika-branching-modal__form">
            <div className="kuika-branching-modal__form-item">
              <KuikaInput
                type="textarea"
                placeholder="Commit message (required to commit & create)"
                value={commitMessage}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  onCommitMessageChange(e.target.value)
                }
                disabled={inProgress}
                autoFocus
              />
            </div>
          </div>
        </div>
      </div>
    </KModal>
  );
};

export default CreateBranchUncommittedModal;
